/**
 * components/rightbar/FilesTabBody.tsx —— 产物视图 tab 正文（kind=`files`）。
 *
 * 真源：`102:frontend_src/src/components/rightbar/FilesTabBody.vue`（365 行）——抄形态/尺寸/交互，译 Vue→React+TS。
 * 依据：设计件 §6.4「产物视图」：**本会话产物列表**（20260920 页头不再重复标题「本会话产物」——
 *       承大王令「去掉本会话产物」：tab 胶囊已给身份，页头只留计数与刷新）（`GET /api/files?session_id=&source=&offset=&limit=`），
 *       按后端注入的 `group`/`source` 分组（0825 定案，分组定义**单一事实源＝后端** `/files/group-meta`）；
 *       **行序＝group 序 ＋ 稳定名序**；打开 ⇒ 开 `sononux://file/session/<sid>/id/<file_id>`（落在**该 tab 自己的格**）；
 *       条目上限截断 ⇒ **标记**；空集**如实说明**；失败按**错误码**分行；🔴 **不建目录树**。
 *      ／§6.3（对外动作走 HTTP 端点：下载／新标签页打开）／§2.4（正文座位输入 `useTabInfo()`；组件三戒）。
 * 现状→新树（§2.4 表）：`ArtifactGroups.vue`(122 行) 的功能**并入本座位**（不丢功能）。
 *
 * Vue→React 差异点（登记）：
 *   ① 零取数（任务书 §2-3）：源件 `load()` 的 `listArtifacts`＋`artifactGroupMeta`＋`buildArtifactList`
 *      投影**移宿主**（102 `session/verbs.js`／`assembly/models/artifactListModel.js` 同批件，随包未投）
 *      ⇒ 本件只吃 `model`（buildArtifactList 产物同形）＋`state`/`errorCode`/`errorText`；
 *      判定与文案（无会话/失败兜底/截断/空集）逐条留在本件，同源件 load() 判序。
 *   ② `reloadToken`＋watch 刷新 ⇒ `onRefresh` 回调（刷新取数归宿主）；`downloadArtifact`（唯一实现）
 *      ⇒ `onDownload(row)` 回调（动作实现在宿主同支）；`info.openAddress` 经座位输入面回调（同源件）。
 *   ③ `v-show` ⇒ `style={{display:'none'}}`（保 DOM，语义同源件）；`v-for`/`v-if` ⇒ map/条件渲染。
 *   ④ `XgIcon`（`components/icons/XgIcon.vue`，随包未投）⇒ 逐字译入本件（路径表 11 枚＋兜底照抄，
 *      16×16／stroke=currentColor／1.5／圆头圆角，形制逐字）；工作面禁建 icons/ 目录 ⇒ 定居本件，
 *      `TextTabBody` 复用。
 */
import React, { useState } from "react";
import type { TabBodyProps } from "./TabBodySeat";

/** 图标路径表（name → path 数组；全部按 16×16 网格自绘，禁第三方库——逐字承源件 XgIcon.vue::PATHS） */
const ICON_PATHS: Record<string, string[]> = Object.freeze({
  file: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M9.5 2v3h3",
  ],
  image: [
    "M3.5 4.5h9v7h-9z",
    "M6 8.3a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z",
    "M4 11l3-3 2.5 2.5L11 8l1.5 1.5",
  ],
  sheet: ["M3.5 4.5h9v7h-9z", "M3.5 7.5h9", "M6.5 4.5v7"],
  archive: ["M3.5 3.5h9v2h-9z", "M4.2 5.5h7.6v6.5H4.2z", "M7 8h2"],
  audio: [
    "M6.5 11V4.8l4.5-1V10",
    "M6.5 11a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0Z",
    "M11 10a1.4 1.4 0 1 1-2.8 0 1.4 1.4 0 0 1 2.8 0Z",
  ],
  video: ["M3.5 5.5h9v6h-9z", "M6.8 7.4l2.8 1.6-2.8 1.6z"],
  pdf: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M6.2 9.6h3.6",
  ],
  doc: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M6.2 8.2h3.6",
    "M6.2 10.4h2.6",
  ],
  ppt: [
    "M9.5 2H5a1.5 1.5 0 0 0-1.5 1.5v9A1.5 1.5 0 0 0 5 14h6a1.5 1.5 0 0 0 1.5-1.5V5L9.5 2Z",
    "M6.4 10.8V7.4h1.8a1.4 1.4 0 0 1 0 2.8H6.4",
  ],
  paperclip: [
    "M10.8 5.6 7.1 9.3a1.5 1.5 0 1 0 2.1 2.1l3.9-3.9a3 3 0 0 0-4.2-4.2l-3.9 3.9a4.5 4.5 0 0 0 6.4 6.4l3.2-3.2",
  ],
  mic: [
    "M8 2.4a1.5 1.5 0 0 1 1.5 1.5v3.4a1.5 1.5 0 0 1-3 0V3.9A1.5 1.5 0 0 1 8 2.4Z",
    "M4.6 7.4a3.4 3.4 0 0 0 6.8 0",
    "M8 10.8v2.4",
  ],
});

/** 统一图标集（承源件 XgIcon.vue：viewBox 0 0 16 16，stroke=currentColor，stroke-width 1.5，圆头圆角） */
export function XgIcon(props: { name?: string; size?: number | string }): React.ReactElement {
  const d = ICON_PATHS[props.name ?? ""] ?? ICON_PATHS.file; // 未登记名 ⇒ 回退 file（承源件）
  const size = props.size ?? 16;
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true" className="xg-icon">
      {d.map((p, i) => (
        <path key={i} d={p} stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      ))}
    </svg>
  );
}

/** 产物行模型（承源件 `buildArtifactList`→`artifactRow` 产物同形） */
export interface ArtifactRow {
  fileId: string;
  filename: string;
  source?: string;
  group?: string;
  size?: number;
  sizeText?: string;
  /** XgIcon 图标名（102 `utils/file.js::fileIcon`） */
  icon?: string;
  createdAt?: string;
  /** 行外右下角小号时间戳文案（源件 20260921 形态） */
  createdAtText?: string;
  fileUrl?: string;
  address?: string;
}

/** 产物分组（承源件列表模型分组同形） */
export interface ArtifactGroup {
  key: string;
  defaultExpanded?: boolean;
  sources?: string[];
  rows: ArtifactRow[];
  count: number;
}

/** 列表模型（承源件 `buildArtifactList` 返回同形：分组＋截断标记＋空集标记） */
export interface ArtifactListModel {
  groups: ArtifactGroup[];
  rows?: ArtifactRow[];
  total: number;
  shown: number;
  truncated: boolean;
  empty: boolean;
}

export interface FilesTabBodyProps extends TabBodyProps {
  /** 列表模型（宿主经 `buildArtifactList` 装配后注入；零取数） */
  model?: ArtifactListModel | null;
  /** 视图状态（承源件 `view.state`：loading（加载中）／ready（有数据或空集）／error（按错误码分行）） */
  state?: "loading" | "ready" | "error";
  errorCode?: string | number | null;
  errorText?: string;
  /** 刷新（源件 reloadToken+1 ⇒ load()；零取数 ⇒ 宿主重拉后换注 model） */
  onRefresh?: () => void;
  /** 下载（源件 `utils/fileActions.js::downloadArtifact` 唯一实现 ⇒ 宿主同支） */
  onDownload?: (row: ArtifactRow) => void | Promise<void>;
}

/** 空模型（承源件 view.model 初值逐字） */
const EMPTY_MODEL: ArtifactListModel = { groups: [], rows: [], total: 0, shown: 0, truncated: false, empty: true };

/** 产物视图 tab 正文 */
export function FilesTabBody(props: FilesTabBodyProps): React.ReactElement {
  const { info } = props;
  const model = props.model ?? EMPTY_MODEL;
  /** 用户折叠的组（初值取自后端 `defaultExpanded`；承源件 isCollapsed 判定逐字） */
  const [collapsedMap, setCollapsedMap] = useState<Record<string, boolean>>({});

  /** 会话 id 来自座位输入面（产物视图是**页类型**，会话由停靠面宿主注入，非 tab 参数） */
  const sessionId = info.sessionId || null;

  function isCollapsed(group: ArtifactGroup): boolean {
    const v = collapsedMap[group.key];
    return v === undefined ? group.defaultExpanded !== true : v;
  }

  function toggleGroup(group: ArtifactGroup): void {
    setCollapsedMap({ ...collapsedMap, [group.key]: !isCollapsed(group) });
  }

  /** 打开产物 ⇒ 地址即身份（§6.3）：落到**本 tab 自己的格**（`openAddress` 由座位输入面提供） */
  function open(row: ArtifactRow): void {
    if (row.address) info.openAddress(row.address);
  }

  /** 下载：走**唯一实现**（对外动作用 HTTP 端点，§6.3 对外边界；★20260922 与正文区产物卡同支） */
  function download(row: ArtifactRow): void {
    if (!row.fileUrl && !row.fileId) return;
    void props.onDownload?.(row);
  }

  /** 视图态判定（承源件 load() 判序：无会话 ⇒ 失败码分行 ⇒ 加载中 ⇒ 空集 ⇒ 列表） */
  const state = props.state ?? "loading";
  let body: "loading" | "ready" | "error" = "ready";
  let errorText = "";
  let errorCode: string | number | null = null;
  if (!sessionId) {
    body = "error";
    errorText = "未选择会话（产物视图按会话作用域）";
  } else if (state === "error") {
    body = "error";
    errorText = props.errorText || "加载产物列表失败";
    errorCode = props.errorCode ?? null;
  } else if (state === "loading") {
    body = "loading";
  }

  return (
    <div className="xg-files-tab" data-testid="rightbar-files">
      <header className="xg-files-tab__head">
        <span className="xg-files-tab__count" data-testid="rightbar-files-count">
          {model.shown} / {model.total}
        </span>
        <button
          type="button"
          className="xg-files-tab__refresh"
          aria-label="刷新产物列表"
          data-testid="rightbar-files-refresh"
          onClick={() => props.onRefresh?.()}
        >
          刷新
        </button>
      </header>

      {/* 失败按错误码分行（§6.4）：码值＋人话文案，两者都展示（文案不参与判定） */}
      {body === "error" ? (
        <p className="xg-files-tab__error" role="alert" data-testid="rightbar-files-error">
          {errorText}
          {errorCode !== null ? <span>（错误码 {errorCode}）</span> : null}
        </p>
      ) : body === "loading" ? (
        <p className="xg-files-tab__hint" data-testid="rightbar-files-loading">
          加载中…
        </p>
      ) : model.empty ? (
        /* 空集如实说明（不画空分组、不假装有内容） */
        <p className="xg-files-tab__hint" data-testid="rightbar-files-empty">
          本会话暂无产物
        </p>
      ) : (
        <div className="xg-files-tab__body" data-testid="rightbar-files-list">
          {model.groups.map((g) => (
            <section key={g.key} className="xg-files-tab__group">
              <button
                type="button"
                className="xg-files-tab__group-head"
                aria-expanded={!isCollapsed(g)}
                data-group-key={g.key}
                onClick={() => toggleGroup(g)}
              >
                <span className="xg-files-tab__group-name">{g.key}</span>
                <span className="xg-files-tab__group-count">{g.count}</span>
              </button>
              {/* v-show ⇒ display:none（保 DOM 同源件） */}
              <ul className="xg-files-tab__rows" style={{ display: isCollapsed(g) ? "none" : undefined }}>
                {g.rows.map((row) => (
                  <li key={row.fileId} className="xg-files-tab__row" data-file-id={row.fileId}>
                    <button type="button" className="xg-files-tab__open" title={row.filename} onClick={() => open(row)}>
                      <span className="xg-files-tab__icon" aria-hidden="true">
                        <XgIcon name={row.icon} />
                      </span>
                      <span className="xg-files-tab__name">{row.filename}</span>
                      <span className="xg-files-tab__size">{row.sizeText}</span>
                    </button>
                    <button
                      type="button"
                      className="xg-files-tab__download"
                      aria-label={`下载${row.filename}`}
                      data-download-file-id={row.fileId}
                      onClick={() => download(row)}
                    >
                      下载
                    </button>
                    {/* ★20260921（大王报障② · 形态拍板「在文件外，右下侧给一个小号的时间戳」）：
                        时间戳**不占文件行**（另起一行、靠右下、caption 档），行内只留 名／大小／下载 */}
                    {row.createdAtText ? (
                      <span className="xg-files-tab__time" data-file-time={row.fileId}>
                        {row.createdAtText}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {/* 条目上限截断 ⇒ 标记（不静默） */}
          {model.truncated ? (
            <p className="xg-files-tab__hint" data-testid="rightbar-files-truncated">
              仅显示前 {model.shown} 项（共 {model.total} 项）
            </p>
          ) : null}
        </div>
      )}
      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 逐字译入；真名 token；`xg-files-tab` 前缀隔离） */
import { filesTabBodyStyle as STYLE } from "../../styles/inlineStyles";

export default FilesTabBody;
